import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useUploadAttachmentMutation } from "@/entities/attachments/hooks";
import {
  AttachmentInfo,
  AttachmentWithStatus,
  SavedAttachmentInfo
} from "@/entities/attachments/types";
import { CloudUpload } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import FileItem from "@/components/controls/FileUpload/FileItem";
import { FileItemStack } from "@/components/controls/FileUpload/FileItemStack";
import { FileDropZone } from "@/components/controls/FileUpload/FileDropZone";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  formatAcceptedFormats,
  formatBytes,
  getErrorByCode
} from "@/entities/attachments/helpers";
import Text from "@/components/ui/text";

const ALLOWED_FILE_TYPES =
  ".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.mov";

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

// The large, non-preview list caps to this many rows before collapsing the
// rest behind "Show N more files" — matches the design's capped-list-plus-link
// pattern instead of letting the stack grow or scroll to reveal everything.
const MAX_VISIBLE_ATTACHMENTS = 5;

interface RejectedFile {
  file: File;
  message: string;
}

// A transport failure carries no response at all, so every level is optional.
interface UploadFailure {
  response?: { status?: number };
}

interface Props {
  onFileSelect?: (attachments: AttachmentInfo[]) => void;
  onAttachmentsChange?: (attachments: SavedAttachmentInfo[]) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  onRawFileSelect?: (file: File) => void;
  externalError?: string | null;
  initialAttachments?: SavedAttachmentInfo[];
  isSingleFile?: boolean;
  isReadOnly?: boolean;
  isPreviewLayout?: boolean;
  dragAndDropText?: string;
  description?: React.ReactNode;
  accept?: string;
  showFileInFullSize?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
  /** Largest single file accepted, in bytes. */
  maxSizeBytes?: number;
  /** Most files the field holds in total. Unset means no ceiling. */
  maxFiles?: number;
  /** Selector stem for the listed files, so each row addresses uniquely inside
   *  the field that owns it. */
  dataTestPrefix?: string;
  /** Overrides the Small upload button's copy. Fields whose label is configured
   *  per account supply it here; unset, the button spells the design's own. */
  label?: string;
}

export const FileUpload: React.FC<Props> = ({
  onFileSelect,
  onAttachmentsChange,
  onUploadingChange,
  onRawFileSelect,
  externalError = null,
  initialAttachments = [],
  isSingleFile = false,
  isReadOnly = false,
  isPreviewLayout = false,
  label,
  dragAndDropText,
  description,
  accept = ALLOWED_FILE_TYPES,
  showFileInFullSize = false,
  disabled = false,
  disabledTooltipText,
  maxSizeBytes = MAX_UPLOAD_BYTES,
  maxFiles,
  dataTestPrefix
}) => {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<AttachmentWithStatus[]>([]);
  // Sticky once revealed: re-collapsing after the user asked to see everything
  // would hide a file they just expanded the list to look at.
  const [showAllAttachments, setShowAllAttachments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef<boolean>(false);
  const userChangedRef = useRef<boolean>(false);
  const pendingIdCounter = useRef<number>(0);
  const publishedCompletionRef = useRef<AttachmentWithStatus[] | null>(null);
  // Ids the parent has been seen holding. An id that has never appeared here
  // is one the parent has not acknowledged yet, not one it dropped.
  const acknowledgedIdsRef = useRef<Set<string>>(new Set());

  // Client-side id for an entry that has no server id yet (in-flight or
  // rejected). The prefix keeps it clear of the server ids that replace it.
  const createPendingId = () => {
    pendingIdCounter.current += 1;
    return `pending-${pendingIdCounter.current}`;
  };

  const isSingleFileUploading =
    isSingleFile && attachments.some((attachment) => attachment?.isUploading);
  const isSingleFileError =
    isSingleFile &&
    (attachments.some((attachment) => attachment?.error) ||
      Boolean(externalError));
  // A field-level failure handed down by the owner, with no row of its own to
  // sit on — and it clears the attachments, so the frame is the only place
  // left that can explain what happened. Per-row errors stay on their rows;
  // only this one turns the frame red.
  const hasExternalError = Boolean(externalError);
  // Anything occupying the single-file slot: a file already on the field, or
  // one still uploading into it. An in-flight entry has to count — otherwise a
  // second file can be picked before the first lands and both get persisted.
  // Failed entries are not the value and free the slot again.
  const hasStoredOrPendingFile = attachments.some(
    (attachment) => !attachment?.error
  );
  // A file that has landed and is not failing — what actually fills the slot
  // the single-file drop zone gives up. Named once because the drop zone's own
  // mount condition and the fallback error line below have to agree: if one
  // reads "filled" differently from the other, a rejection either shows twice
  // or not at all.
  const hasSettledFile = attachments.some(
    (attachment) => !attachment?.isUploading && !attachment?.error
  );
  const firstAttachmentError = attachments.find(
    (attachment) => attachment?.error
  )?.error;

  const { mutateAsync: uploadAttachment } = useUploadAttachmentMutation();

  // Helper text derives from the same limits the field enforces, so a caller
  // that narrows the formats or the ceiling cannot leave the copy claiming the
  // old ones. An explicit `description` still wins.
  const acceptedFormats = formatAcceptedFormats(accept);
  // A field with no format restriction still has a size ceiling to state.
  const defaultDescription = acceptedFormats
    ? t("filesUpload.acceptedFormats", {
        formats: acceptedFormats,
        size: formatBytes(maxSizeBytes)
      })
    : t("filesUpload.maxSize", { size: formatBytes(maxSizeBytes) });
  const dropZoneLabels = {
    dragAndDrop:
      dragAndDropText ??
      (isSingleFile ? t("filesUpload.dndSingle") : t("filesUpload.dnd")),
    browseFiles: t("filesUpload.browseFiles"),
    uploading: t("filesUpload.uploading"),
    uploadFailed: t("filesUpload.uploadFailed"),
    tryAgain: t("filesUpload.tryAgain")
  };

  useEffect(() => {
    if (!initializedRef.current && initialAttachments?.length > 0) {
      const mappedAttachments = initialAttachments.map((attachment) => ({
        ...attachment,
        isUploading: false
      }));
      setAttachments(mappedAttachments);
      acknowledgedIdsRef.current = new Set(
        mappedAttachments.map((attachment) => attachment.id)
      );
      initializedRef.current = true;
    }
  }, [initialAttachments]);

  useEffect(() => {
    if (!initializedRef.current) return;
    // The parent owns the stored set. A file removed from the field while this
    // list is still mounted has to leave the internal list too, or the next
    // upload republishes it and the removal is undone. Pruning rather than
    // reseeding keeps in-flight and failed entries, which the parent has no
    // record of yet.
    const storedIds = new Set(
      (initialAttachments ?? []).map((attachment) => attachment.id)
    );
    // Absence only means "removed" for a file the parent was previously holding.
    // A file that has just finished uploading is published asynchronously, so
    // between the completion and the parent's write-back it is legitimately
    // missing from the stored set — pruning on absence alone deletes it from
    // the list the moment it arrives, and the field looks like it ignored the
    // upload.
    const acknowledgedIds = acknowledgedIdsRef.current;
    acknowledgedIdsRef.current = storedIds;
    setAttachments((previousAttachments) => {
      const keptAttachments = previousAttachments.filter(
        (attachment) =>
          attachment.isUploading ||
          attachment.error ||
          storedIds.has(attachment.id) ||
          !acknowledgedIds.has(attachment.id)
      );
      // Same identity when nothing was pruned: a fresh array would read as a
      // new completion and re-publish on every render.
      return keptAttachments.length === previousAttachments.length
        ? previousAttachments
        : keptAttachments;
    });
  }, [initialAttachments]);

  useEffect(() => {
    const hasUploading = attachments.some(
      (attachment) => attachment.isUploading
    );
    onUploadingChange?.(hasUploading);

    if (!userChangedRef.current) return;

    const completedAttachments = attachments.filter(
      (attachment) => !attachment.isUploading
    );
    const allUploadsComplete = !hasUploading;

    if (
      allUploadsComplete &&
      (completedAttachments.length > 0 || attachments.length === 0)
    ) {
      // Each state update produces a fresh array, so its identity is the
      // completion's key: publishing at most once per array keeps a disabled
      // toggle from re-emitting a completion that already went out.
      if (publishedCompletionRef.current === attachments) return;
      // An upload finishing after the permission lock lands is held, not
      // dropped — re-enabling the field flushes it, so the file that did
      // finish still gets persisted.
      if (disabled) return;

      publishedCompletionRef.current = attachments;
      onFileSelect?.(completedAttachments);
      // Entries that never uploaded (rejected type, failed request) have no
      // server id — persisting them would save a bogus attachment reference.
      onAttachmentsChange?.(
        completedAttachments
          .filter((attachment) => !attachment.error)
          .map((attachment) => ({
            id: attachment.id,
            filename: attachment.filename
          }))
      );
    }
  }, [
    attachments,
    disabled,
    onUploadingChange,
    onFileSelect,
    onAttachmentsChange
  ]);

  useEffect(() => {
    if (!externalError) return;
    // Preview mode has no room for a detail line under the compact button, so
    // the owner's message is toasted the same way an upload failure is. The
    // button still moves to its error state below — without that the field
    // would drop every held file with nothing on screen to say why.
    if (isPreviewLayout) {
      toast.error(externalError);
    }
    if (attachments.length > 0) {
      setAttachments([]);
    }
  }, [externalError]);

  // The `accept` attribute on the input is only a picker hint — OS dialogs can
  // override it, and drag-and-drop bypasses it entirely. Enforce it here, at
  // the single choke point both entry paths go through. `accept` takes both
  // extensions and MIME types, so both forms are honoured; an empty allowlist
  // means no restriction.
  const acceptEntries = accept
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0);
  const acceptedExtensions = acceptEntries.filter((entry) =>
    entry.startsWith(".")
  );
  const acceptedMimeTypes = acceptEntries.filter((entry) =>
    entry.includes("/")
  );

  const matchesAccept = (file: File) => {
    const filename = file.name.toLowerCase();
    if (acceptedExtensions.some((extension) => filename.endsWith(extension))) {
      return true;
    }
    // Browsers leave `type` empty for files they cannot classify, which must
    // not match a wildcard rule the file was never shown to satisfy.
    const fileType = file.type.toLowerCase();
    if (!fileType) {
      return false;
    }
    return acceptedMimeTypes.some((mimeType) =>
      mimeType.endsWith("/*")
        ? fileType.startsWith(mimeType.slice(0, -1))
        : fileType === mimeType
    );
  };

  // Splits a selection into the files that may be uploaded and the ones that
  // cannot, each rejection carrying the reason it will be shown with. Every
  // rule is evaluated per file so a mixed batch keeps the files that pass.
  const classifyFiles = (newFiles: File[]) => {
    const hasTypeRestriction =
      acceptedExtensions.length > 0 || acceptedMimeTypes.length > 0;
    // Names already on the field, plus the ones claimed earlier in this same
    // batch — two copies of one name arriving together are still duplicates.
    const takenNames = new Set(
      attachments
        .filter((attachment) => !attachment?.error)
        .map((attachment) => attachment.filename.toLowerCase())
    );
    const settledCount = attachments.filter(
      (attachment) => !attachment?.error
    ).length;
    let remainingSlots =
      maxFiles === undefined
        ? Number.POSITIVE_INFINITY
        : Math.max(maxFiles - settledCount, 0);

    const acceptedFiles: File[] = [];
    const rejectedFiles: RejectedFile[] = [];

    newFiles.forEach((file) => {
      const reject = (message: string) => rejectedFiles.push({ file, message });
      if (hasTypeRestriction && !matchesAccept(file)) {
        reject(t("filesUpload.fileTypeNotSupported"));
        return;
      }
      // A zero-byte file uploads without error and then fails to open, so it
      // is refused at the field rather than persisted as a broken attachment.
      if (file.size === 0) {
        reject(t("filesUpload.emptyFile"));
        return;
      }
      // The size limit is stated to the user next to the field, so catch it
      // here rather than letting the upload fail with a generic server error.
      if (file.size > maxSizeBytes) {
        reject(
          t("filesUpload.fileTooLarge", { size: formatBytes(maxSizeBytes) })
        );
        return;
      }
      if (takenNames.has(file.name.toLowerCase())) {
        reject(t("filesUpload.duplicateFile"));
        return;
      }
      if (remainingSlots <= 0) {
        reject(t("filesUpload.maxFilesReached", { max: maxFiles }));
        return;
      }
      remainingSlots -= 1;
      takenNames.add(file.name.toLowerCase());
      acceptedFiles.push(file);
    });

    return { acceptedFiles, rejectedFiles };
  };

  // A rejected file only becomes an inline error where the layout has somewhere
  // to put one. Preview mode has no room by design, so an added row would be
  // discarded silently and the file would look like it simply vanished. The
  // drop-zone layout always has room: multi-file lists the row, and single-file
  // filters it out of the list but paints the frame red and prints the message
  // in its own banner, which is where that layout reports every failure.
  const rejectionsRenderInline = !isPreviewLayout;

  const addRejectedAttachments = (rejectedFiles: RejectedFile[]) => {
    if (rejectedFiles.length === 0) return;
    if (!rejectionsRenderInline) {
      // One toast per reason rather than per file: dropping a folder of
      // unsupported files would otherwise bury the screen under a stack of
      // toasts all saying the same thing.
      const filenamesByMessage = new Map<string, string[]>();
      rejectedFiles.forEach(({ file, message }) => {
        filenamesByMessage.set(message, [
          ...(filenamesByMessage.get(message) ?? []),
          file.name
        ]);
      });
      filenamesByMessage.forEach((filenames, message) =>
        toast.error(`${filenames.join(", ")}: ${message}`)
      );
      return;
    }
    setAttachments((previousAttachments) => [
      ...previousAttachments,
      ...rejectedFiles.map(({ file, message }) => ({
        id: createPendingId(),
        filename: file.name,
        isUploading: false,
        error: message
      }))
    ]);
  };

  const markUploaded = (pendingId: string, filename: string, id: string) => {
    setAttachments((previousAttachments) =>
      previousAttachments.map((attachment) =>
        attachment.id === pendingId
          ? { id, filename, isUploading: false }
          : attachment
      )
    );
  };

  const markUploadFailed = (pendingId: string, error: unknown) => {
    // Store the response status, not the raw error: the failure message is
    // resolved from the status code, so an error object renders a red row with
    // no explanation at all.
    const statusCode = (error as UploadFailure)?.response?.status;
    // Only a positive status survives: every later check reads `error` for
    // truthiness, so a 0 status would store as "no error" and let the failed
    // row's client-only pending id be published as if it had uploaded.
    const uploadError =
      typeof statusCode === "number" && statusCode > 0
        ? statusCode
        : t("attachments.errors.somethingWentWrong");
    // Preview mode reports failures as a toast rather than as a red row, so
    // the failed entry leaves the list instead of occupying a slot in a field
    // that has room for the stored files only.
    if (isPreviewLayout) {
      toast.error(getErrorByCode(t, uploadError));
      setAttachments((previousAttachments) =>
        previousAttachments.filter((attachment) => attachment.id !== pendingId)
      );
      return;
    }
    setAttachments((previousAttachments) =>
      previousAttachments.map((attachment) =>
        attachment.id === pendingId
          ? {
              ...attachment,
              isUploading: false,
              error: uploadError
            }
          : attachment
      )
    );
  };

  const uploadFile = (file: File) => {
    onRawFileSelect?.(file);
    // Two files picked in one selection can share a name, so the in-flight
    // entry is tracked by its own id — keyed on the filename, the first upload
    // to land would overwrite both entries and strand the second.
    const pendingId = createPendingId();
    setAttachments((previousAttachments) => [
      ...previousAttachments,
      { id: pendingId, filename: file.name, isUploading: true }
    ]);

    const formData = new FormData();
    formData.append("file", file);
    (async () => {
      try {
        const response = await uploadAttachment({ data: formData });
        markUploaded(pendingId, file.name, response.id);
      } catch (error) {
        markUploadFailed(pendingId, error);
      }
    })();
  };

  const handleFiles = (newFiles: File[]) => {
    userChangedRef.current = true;
    // A failed entry survives in state so the single-file layout can name the
    // failure in its banner, but it belongs to the drop that produced it. The
    // next drop is the user answering that failure, so retire the old error
    // before this drop records its own — otherwise a retry that succeeds leaves
    // the previous message stranded under the file that replaced it, and the
    // list filters the failed row out, so nothing on screen can dismiss it.
    // Only the single-file layout needs this: multi-file lists its failed rows
    // with their own remove control, and preview mode never keeps them at all.
    // Both the slot count and the duplicate-name set already ignore errored
    // entries, so clearing them cannot change how this batch is classified.
    if (isSingleFile) {
      setAttachments((previousAttachments) =>
        previousAttachments.filter((attachment) => !attachment?.error)
      );
    }
    const { acceptedFiles, rejectedFiles } = classifyFiles(newFiles);
    addRejectedAttachments(rejectedFiles);
    // A single-file field holds one file in total, not one per selection — a
    // file already on the field or still uploading into it counts, so a second
    // pick has to be dropped until the existing one is removed.
    const filesToUpload = isSingleFile
      ? hasStoredOrPendingFile
        ? []
        : acceptedFiles.slice(0, 1)
      : acceptedFiles;
    // The drop zone stays mounted while the first file is still uploading, so a
    // file dropped into that window is accepted by the picker and then dropped
    // here — say so rather than letting it disappear.
    if (isSingleFile && acceptedFiles.length > filesToUpload.length) {
      addRejectedAttachments(
        acceptedFiles.slice(filesToUpload.length).map((file) => ({
          file,
          message: t("filesUpload.singleFileOnly")
        }))
      );
    }
    filesToUpload.forEach(uploadFile);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newFiles = e.target.files ? Array.from(e.target.files) : [];
    // The list is copied above, so clearing the input here is safe — and it is
    // what makes "Try again" work: an unchanged input value suppresses `change`,
    // so re-picking the file that just failed would never reach this handler.
    e.target.value = "";
    if (newFiles.length > 0) {
      handleFiles(newFiles);
    }
  };

  const handleBrowseClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const removeFile = (attachmentId: string) => {
    if (isReadOnly || disabled) return;
    userChangedRef.current = true;
    setAttachments((previousAttachments) =>
      previousAttachments.filter((attachment) => attachment.id !== attachmentId)
    );
  };

  const getSortPriority = (attachment: AttachmentWithStatus) => {
    if (attachment?.error) return 2;
    if (attachment?.isUploading) return 1;
    return 0;
  };

  const renderAttachmentList = (
    attachmentList: AttachmentWithStatus[],
    isFullSize: boolean,
    removeHandler: (id: string) => void,
    isGrouped = false
  ) => {
    return [...attachmentList]
      .sort((a, b) => getSortPriority(a) - getSortPriority(b))
      .map((attachment) => (
        <FileItem
          key={attachment.id}
          attachment={attachment}
          onRemove={removeHandler}
          size={isPreviewLayout ? "small" : "large"}
          isFullSize={isFullSize}
          hideRemove={isReadOnly}
          // Small chips carry the remove control only; the design keeps
          // download to the Large row, where there is room for both.
          hideDownload={isPreviewLayout}
          isGrouped={isGrouped}
          removeDisabled={disabled}
          removeDisabledTooltipText={disabledTooltipText}
          dataTest={
            dataTestPrefix ? `${dataTestPrefix}-${attachment.id}` : undefined
          }
        />
      ));
  };

  return (
    <div
      className="w-full flex flex-col gap-4"
      data-test="file-upload-container"
    >
      {isPreviewLayout && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            multiple={!isSingleFile}
            accept={accept}
          />
          {/* Preview mode opens on the Small upload button rather than the
              Large drag-and-drop area, for the compact surfaces that have no
              vertical room for the frame. Once a file is listed the button
              gives way to the stack, and adding more is offered as a link
              below it. */}
          {!isReadOnly && attachments.length === 0 && (
            <FileDropZone
              size="small"
              label={
                label ??
                (isSingleFile
                  ? t("filesUpload.uploadFile")
                  : t("filesUpload.uploadFiles"))
              }
              onFiles={handleFiles}
              labels={dropZoneLabels}
              state={
                isSingleFileUploading
                  ? "uploading"
                  : isSingleFileError || hasExternalError
                    ? "error"
                    : "default"
              }
              accept={accept}
              multiple={!isSingleFile}
              disabled={disabled}
              disabledTooltipText={disabledTooltipText}
              dataTest="file-upload-small-button"
            />
          )}
        </>
      )}
      {attachments?.length > 0 &&
        (!isSingleFile && !isPreviewLayout ? (
          (() => {
            // Sort once here so the same order decides both what's visible and
            // what renders — renderAttachmentList's own sort is a no-op on an
            // already-sorted slice, but capping the unsorted prop could hide a
            // different file than the one actually rendered on top.
            const sortedAttachments = [...attachments].sort(
              (leftAttachment, rightAttachment) =>
                getSortPriority(leftAttachment) - getSortPriority(rightAttachment)
            );
            // Only settled attachments count against the cap — an uploading or
            // failed attachment must stay visible so its progress/error state
            // (rendered per-row, the only such indicator in multi-file mode)
            // is never silently pushed behind "Show N more files". Read-only
            // surfaces never cap at all — they keep the pre-existing grouped
            // FileItemStack container but show every attachment unconditionally.
            const settledAttachments = sortedAttachments.filter(
              (attachment) => getSortPriority(attachment) === 0
            );
            const unsettledAttachments = sortedAttachments.filter(
              (attachment) => getSortPriority(attachment) !== 0
            );
            const visibleSettledAttachments =
              showAllAttachments || isReadOnly
                ? settledAttachments
                : settledAttachments.slice(0, MAX_VISIBLE_ATTACHMENTS);
            const visibleAttachments = [
              ...visibleSettledAttachments,
              ...unsettledAttachments
            ];
            const hiddenCount = isReadOnly
              ? 0
              : settledAttachments.length - visibleSettledAttachments.length;
            return (
              <>
                <FileItemStack className="max-w-[720px] overflow-x-hidden">
                  {renderAttachmentList(visibleAttachments, true, removeFile, true)}
                </FileItemStack>
                {hiddenCount > 0 && (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto w-fit text-left"
                    onClick={() => setShowAllAttachments(true)}
                    data-test="file-upload-show-remaining-button"
                  >
                    {t("filesUpload.showRemainingFiles", {
                      count: hiddenCount
                    })}
                  </Button>
                )}
              </>
            );
          })()
        ) : (
          <div
            className={cn(
              "flex flex-col",
              isPreviewLayout && "gap-2 max-h-52 overflow-y-auto"
            )}
          >
            {!isSingleFile &&
              renderAttachmentList(attachments, true, removeFile)}
            {isSingleFile &&
              renderAttachmentList(
                attachments.filter(
                  (attachment) =>
                    // The drop-zone layout reports progress and failures in its
                    // own banner below, so the list there carries settled files
                    // only. Preview mode has no banner and hides its upload
                    // button once a file is picked — filtering there would
                    // leave the field blank for the length of the upload.
                    isPreviewLayout ||
                    (!attachment?.isUploading && !attachment?.error)
                ),
                showFileInFullSize,
                removeFile
              )}
          </div>
        ))}
      {isPreviewLayout &&
        !isReadOnly &&
        attachments.length > 0 &&
        !(isSingleFile && hasStoredOrPendingFile) &&
        (() => {
          // The design hovers this as a text link, not as a pill — suppress the
          // tertiary background hover and fade the label instead, so the hover
          // carries no color of its own.
          const browseButton = (
            <Button
              variant="tertiary"
              className="w-fit h-fit flex gap-1 hover:bg-transparent hover:opacity-80 transition-opacity"
              onClick={handleBrowseClick}
              disabled={disabled}
              data-test="file-upload-browse-button"
            >
              <IconContainer color="secondary">
                <CloudUpload />
              </IconContainer>
              {t("filesUpload.browseMoreFiles")}
            </Button>
          );
          if (disabled && disabledTooltipText) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{browseButton}</span>
                </TooltipTrigger>
                <TooltipContent>{disabledTooltipText}</TooltipContent>
              </Tooltip>
            );
          }
          return browseButton;
        })()}
      {!isReadOnly &&
        !isPreviewLayout &&
        ((isSingleFile && (!hasSettledFile || hasExternalError)) ||
          !isSingleFile) && (
          <FileDropZone
            onFiles={handleFiles}
            labels={dropZoneLabels}
            state={
              isSingleFileUploading
                ? "uploading"
                : isSingleFileError || hasExternalError
                  ? "error"
                  : "default"
            }
            errorMessage={
              externalError ||
              getErrorByCode(
                t,
                attachments.find((attachment) => attachment?.error)?.error ?? ""
              )
            }
            description={description ?? defaultDescription}
            accept={accept}
            multiple={!isSingleFile}
            disabled={disabled}
            disabledTooltipText={disabledTooltipText}
            dataTest="file-upload-drop-zone"
          />
        )}
      {!isReadOnly &&
        !isPreviewLayout &&
        isSingleFile &&
        hasSettledFile &&
        !hasExternalError &&
        firstAttachmentError && (
          // Once the slot is filled the drop zone unmounts and takes its error
          // banner with it, while the list below carries settled rows only — so
          // a file rejected in the same drop leaves no trace and the user
          // submits believing everything they dropped was accepted.
          <Text className="pl-1" variant="smBodyError" role="alert">
            {getErrorByCode(t, firstAttachmentError)}
          </Text>
        )}
    </div>
  );
};

export default FileUpload;
